import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FileText, FolderClock, CreditCard, LogIn, LogOut, UserPlus, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { usePlatformMode } from '@/hooks/usePlatformMode';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import logo from '@/assets/logo-optimized.webp';
import { EntitlementsBadge } from './EntitlementsBadge';


export function DesktopNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useCurrentUser();
  const { b2cEnabled, b2bEnabled } = usePlatformMode();

  const b2cItems = [
    { to: '/analyze', label: 'Анализ', icon: FileText, match: (p: string) => p.startsWith('/analyze') },
    { to: '/my-reports', label: 'Отчёты', icon: FolderClock, match: (p: string) => p.startsWith('/my-reports') },
    { to: '/tariffs', label: 'Тарифы', icon: CreditCard, match: (p: string) => p.startsWith('/tariffs') || p.startsWith('/pay') },
  ];

  const b2bItems = [
    { to: '/partner/dashboard', label: 'Кабинет клиники', icon: Building2, match: (p: string) => p.startsWith('/partner') },
  ];

  const items = [
    ...(b2cEnabled ? b2cItems : []),
    ...(!b2cEnabled && b2bEnabled ? b2bItems : []),
  ];

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    toast.success('Вы вышли');
    navigate('/');
  };

  return (
    <header className="hidden lg:block sticky top-0 z-40 bg-card/85 backdrop-blur-xl border-b border-border">
      <div className="max-w-6xl mx-auto px-8 h-16 flex items-center justify-between gap-8">
        <Link to="/" className="flex items-center gap-3 group">
          <img
            src={logo}
            alt="АнализПро"
            width={36}
            height={36}
            className="w-9 h-9 rounded-full object-contain shadow-sm"
          />
          <span className="text-lg font-black bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            АнализПро<span className="text-[10px] align-super">©</span>
          </span>
        </Link>

        <nav className="flex items-center gap-1">
          {items.map((item) => {
            const active = item.match(location.pathname);
            const Icon = item.icon;
            return (
              <button
                key={item.to}
                onClick={() => navigate(item.to)}
                className={`px-4 h-10 rounded-xl flex items-center gap-2 text-sm font-semibold transition-all ${
                  active
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
                }`}
              >
                <Icon className="w-4 h-4" />
                {item.label}
              </button>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          {b2cEnabled && <EntitlementsBadge variant="compact" className="hidden xl:inline-flex" />}

          {user ? (
            <Button
              onClick={handleSignOut}
              variant="ghost"
              size="sm"
              className="h-9 px-3 gap-1.5 text-sm"
            >
              <LogOut className="w-4 h-4" />
              Выйти
            </Button>
          ) : (
            <div className="flex items-center gap-0.5 text-sm">
              <button
                onClick={() => navigate('/login')}
                className="h-9 px-2.5 rounded-lg font-medium text-muted-foreground hover:text-foreground hover:bg-muted/60 inline-flex items-center gap-1.5 transition-colors"
              >
                <LogIn className="w-4 h-4" />
                Вход
              </button>
              <span className="text-border">/</span>
              <button
                onClick={() => navigate('/register')}
                className="h-9 px-2.5 rounded-lg font-medium text-muted-foreground hover:text-foreground hover:bg-muted/60 inline-flex items-center gap-1.5 transition-colors"
              >
                <UserPlus className="w-4 h-4" />
                Регистрация
              </button>
            </div>
          )}
          {b2cEnabled ? (
            <Button
              onClick={() => navigate('/analyze')}
              variant="hero"
              size="sm"
              className="ml-1"
            >
              Расшифровать
            </Button>
          ) : b2bEnabled ? (
            <Button
              onClick={() => navigate('/partner/register')}
              variant="hero"
              size="sm"
              className="ml-1"
            >
              Подключить клинику
            </Button>
          ) : null}

        </div>
      </div>
    </header>
  );
}
